import isEmpty from 'lodash-es/isEmpty';
import trim from 'lodash-es/trim';
import { type HTMLAttributes, type ReactNode, useMemo } from 'react';
import { Link } from 'react-router';
import type { EmployeeEditTab } from '@/common/types';

interface Props extends HTMLAttributes<HTMLAnchorElement> {
  employeeId: string | null | undefined;
  tab?: EmployeeEditTab;
  children: ReactNode;
  openInNew?: boolean;
  /** Appends a `highlight_job_history` query parameter carrying this job history record's own ID
   * (FR-071/FR-074, User Story 9, research.md #30) - the Job History tab highlights the matching
   * row when present. Purely additive; omitted entirely when not passed. */
  highlightJobHistoryId?: string;
}

export const EmployeeLink = ({ employeeId, tab, children, openInNew, highlightJobHistoryId, ...props }: Props) => {
  const canonicalId = useMemo(
    () => (!employeeId || isEmpty(trim(employeeId)) || !/^\d+/.test(trim(employeeId)) ? null : trim(employeeId)),
    [employeeId],
  );

  if (!canonicalId) return children;

  const highlightQuery = highlightJobHistoryId
    ? `?highlight_job_history=${encodeURIComponent(highlightJobHistoryId)}`
    : '';

  return (
    <Link
      {...props}
      to={`/employees/edit/${canonicalId}${tab ? `/${tab}` : ''}${highlightQuery}`}
      target={openInNew ? '_blank' : undefined}
    >
      {children}
    </Link>
  );
};
